import { getErrorFromResponse } from '@atlassian/jira-common-directory-v2/src/rest/errors';
import {
	ITEMS_ON_PAGE,
	START_AT,
	ORDER,
	FILTER,
} from '@atlassian/jira-project-directories-components/src/common/constants/default-parameters';
import { createOptions } from '@atlassian/jira-project-directories-components/src/common/rest';

import type { Component } from '../../common/model';
import type { Query } from '../../query-param-adapter/model';
import { mapToOrderFetch } from '../../query-param-adapter/model/constants/order-field-mapper';
import { getBaseUrl } from '../context/selectors';
import { getErrorFromResultJson } from '../errors';
import type { State } from '../reducer';

import { transformComponents, transformComponentObj } from './transformers';

export const createRetrieveUrl = (
	baseUrl: string,
	projectKey: string,
	startAt: number,
	maxResults: number,
	orderBy: string,
	filter: string,
) => {
	const encodedQuery: string = encodeURIComponent(filter);
	const componentSourceParam = '&componentSource=auto';
	return `${baseUrl}/rest/api/2/project/${projectKey}/component?startAt=${startAt}&maxResults=${maxResults}&orderBy=${orderBy}&query=${encodedQuery}${componentSourceParam}`;
};

export type RequestResult = {
	byId: {
		[key: string]: Component;
	};
	list: string[];
	listAri: string[];
	total: number;
};

type GetPrefetchedData = (
	baseUrl: string,
	projectKey: string,
	query?: Query,
) => Promise<RequestResult>;

type RequestComponents = (
	baseUrl: string,
	projectKey: string,
	startAt?: number,
	maxResults?: number,
	orderBy?: string,
	filter?: string,
) => Promise<RequestResult>;

export const requestComponents: RequestComponents = async (
	baseUrl,
	projectKey,
	startAt = START_AT,
	maxResults = ITEMS_ON_PAGE,
	orderBy = ORDER,
	filter = FILTER,
) => {
	const fullUrl = createRetrieveUrl(baseUrl, projectKey, startAt, maxResults, orderBy, filter);
	const response = await fetch(fullUrl, createOptions('GET'));
	const json = await response.json();

	if (!response.ok) {
		throw await getErrorFromResponse(response, 'Failed to fetch');
	}

	const { values, total } = json;
	return transformComponents(values, total, baseUrl);
};

export const getPrefetchedData: GetPrefetchedData = (baseUrl, projectKey, query) => {
	if (query) {
		const { orderDirection, orderField, filter, page } = query;
		const orderBy = mapToOrderFetch(orderField, orderDirection);
		const maxResults = ITEMS_ON_PAGE;
		const startAt = (page - 1) * maxResults;

		return requestComponents(baseUrl, projectKey, startAt, maxResults, orderBy, filter);
	}
	return requestComponents(baseUrl, projectKey);
};

export const createRetrieveOneUrl = (id: string) => `/rest/api/2/component/${id}`;

type RequestComponent = (
	state: State,
	id: string,
) => Promise<{
	component: Component;
	id: string;
}>;

export const requestComponent: RequestComponent = async (state, id) => {
	const baseUrl = getBaseUrl(state);
	const fullUrl = `${baseUrl}${createRetrieveOneUrl(id)}`;
	const response = await fetch(fullUrl, createOptions('GET'));
	const json = await response.json();

	if (response.ok) {
		return {
			id,
			component: transformComponentObj(json, baseUrl),
		};
	}

	throw getErrorFromResultJson(json, response.status);
};
